<template>
  <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-flex-shrink-0">
    <button
      class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-50 tw-rounded-t-xl tw-transition-colors"
      @click="expanded = !expanded"
    >
      <svg class="tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform" :class="expanded ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
      <h3 class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-tracking-wider tw-uppercase">Meerjarenanalyse</h3>
      <span v-if="expanded && isLoading" class="tw-w-3 tw-h-3 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-flex-shrink-0" />
      <template v-else-if="analysis">
        <span class="tw-text-xs tw-bg-gray-100 tw-text-gray-500 tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium">
          {{ analysis.summary.dataSpanYears }} jaar
        </span>
      </template>
    </button>

    <div v-if="expanded" class="tw-px-4 tw-pb-4">
      <div v-if="isLoading" class="tw-text-xs tw-text-gray-400 tw-py-6 tw-text-center">
        <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-inline-block tw-mr-2" />
        Data laden...
      </div>
      <div v-else-if="!analysis" class="tw-text-xs tw-text-gray-400 tw-py-4 tw-text-center">Onvoldoende data.</div>

      <template v-else>
        <div class="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-3">
          <!-- Data span -->
          <div class="tw-rounded-lg tw-p-2.5 tw-bg-blue-50">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-blue-700">Periode</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono tw-text-blue-700">
              {{ analysis.summary.yearsAvailable[0] }}–{{ analysis.summary.yearsAvailable[analysis.summary.yearsAvailable.length - 1] }}
            </div>
            <div class="tw-text-xs tw-text-blue-500">
              {{ analysis.summary.totalDataMonths }} maanden data
            </div>
          </div>

          <!-- Degradation -->
          <div class="tw-rounded-lg tw-p-2.5" :class="degradationBg">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5" :class="degradationIcon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="degradationTextColor">Degradatie</span>
            </div>
            <div class="tw-text-sm tw-font-bold" :class="degradationTextColor">
              {{ degradationLabel }}
            </div>
            <div class="tw-text-xs" :class="degradationSubColor">
              {{ degradationDetail }}
            </div>
          </div>

          <!-- Communication -->
          <div class="tw-rounded-lg tw-p-2.5" :class="analysis.summary.commGapCount > 3 ? 'tw-bg-red-50' : analysis.summary.commGapCount > 0 ? 'tw-bg-amber-50' : 'tw-bg-green-50'">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5" :class="analysis.summary.commGapCount > 3 ? 'tw-text-red-500' : analysis.summary.commGapCount > 0 ? 'tw-text-amber-500' : 'tw-text-green-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="analysis.summary.commGapCount > 3 ? 'tw-text-red-700' : analysis.summary.commGapCount > 0 ? 'tw-text-amber-700' : 'tw-text-green-700'">Data</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="analysis.summary.commGapCount > 3 ? 'tw-text-red-700' : analysis.summary.commGapCount > 0 ? 'tw-text-amber-700' : 'tw-text-green-700'">
              {{ analysis.summary.commGapCount === 0 ? 'Compleet' : `${analysis.summary.commGapCount} gaps` }}
            </div>
            <div class="tw-text-xs" :class="analysis.summary.commGapCount > 0 ? 'tw-text-amber-500' : 'tw-text-green-500'">
              {{ analysis.summary.commGapCount === 0 ? 'geen ontbrekende maanden' : 'maanden zonder data' }}
            </div>
          </div>
        </div>

        <!-- Year-by-year prognosis table -->
        <div v-if="analysis.yearlyPrognosis" class="tw-mb-3">
          <button
            class="tw-text-xs tw-text-gray-400 hover:tw-text-gray-600 tw-flex tw-items-center tw-gap-1 tw-transition-colors"
            @click="showYearTable = !showYearTable"
          >
            <svg class="tw-w-3 tw-h-3 tw-transition-transform" :class="showYearTable ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
            Jaaroverzicht
          </button>
          <div v-if="showYearTable" class="tw-mt-2 tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
            <table class="tw-w-full tw-text-xs">
              <thead>
                <tr class="tw-bg-gray-50 tw-text-gray-500 tw-text-left">
                  <th class="tw-px-2 tw-py-1.5">Jaar</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right">Productie</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right">Verwacht</th>
                  <th class="tw-px-2 tw-py-1.5 tw-w-20">Ratio</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right">Mnd</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="year in Object.keys(analysis.yearlyPrognosis).filter(y => analysis.yearlyPrognosis[y].ratio !== null).sort()"
                  :key="year"
                  class="tw-border-t tw-border-gray-50"
                >
                  <td class="tw-px-2 tw-py-1.5 tw-font-medium tw-text-gray-700">{{ year }}</td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-font-mono tw-text-gray-700">{{ Math.round(analysis.yearlyPrognosis[year].actual / 1000) }} kWh</td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-font-mono tw-text-gray-500">{{ Math.round(analysis.yearlyPrognosis[year].expected / 1000) }} kWh</td>
                  <td class="tw-px-2 tw-py-1.5">
                    <div class="tw-flex tw-items-center tw-gap-1">
                      <div class="tw-flex-1 tw-bg-gray-100 tw-rounded-full tw-h-1.5 tw-overflow-hidden">
                        <div
                          class="tw-h-full tw-rounded-full"
                          :class="ratioBar(analysis.yearlyPrognosis[year].ratio)"
                          :style="{ width: Math.min(analysis.yearlyPrognosis[year].ratio, 150) / 1.5 + '%' }"
                        />
                      </div>
                      <span class="tw-font-mono tw-w-8 tw-text-right tw-font-medium" :class="ratioColor(analysis.yearlyPrognosis[year].ratio)">
                        {{ analysis.yearlyPrognosis[year].ratio }}
                      </span>
                    </div>
                  </td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500">{{ analysis.yearlyPrognosis[year].monthCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Findings -->
        <div v-if="analysis.segments.length > 0" class="tw-space-y-1">
          <div v-for="(seg, idx) in analysis.segments" :key="idx" class="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-py-1">
            <svg v-if="seg.type === 'critical'" class="tw-w-3.5 tw-h-3.5 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
            <svg v-else-if="seg.type === 'warning'" class="tw-w-3.5 tw-h-3.5 tw-text-amber-500 tw-mt-0.5 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
            <svg v-else-if="seg.type === 'good'" class="tw-w-3.5 tw-h-3.5 tw-text-green-500 tw-mt-0.5 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
            <span v-else class="tw-w-3.5 tw-h-3.5 tw-rounded-full tw-bg-gray-300 tw-inline-block tw-mt-0.5 tw-flex-shrink-0" />
            <span class="tw-text-gray-700 tw-leading-relaxed">{{ seg.text }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { format, subYears } from 'date-fns'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { analyzeFiveYear } from './yearlyAnalysis'
import { STATUS_COLORS, ratioBarColor, ratioTextColor } from './statusColors'

const props = defineProps({
  serialNumber: { type: String, required: true },
  // how much of the serial's address the main line covers: address (default, every meter there, a
  // CVZ's fake addresses too), installation or meter; neighbors always cover their own address
  scope: { type: String, default: 'address' },
  mainWp: { type: Number, default: null },
  expectedProduction: { type: Number, default: null },
  dateActivated: { type: String, default: null },
})

const expanded = ref(false)
const showYearTable = ref(false)

const dateFrom = format(subYears(new Date(), 5), 'yyyy-MM-dd')
const dateTill = format(new Date(), 'yyyy-MM-dd')

const { data: monthlyData, isLoading } = useQuery({
  queryKey: computed(() => ['fiveYear', props.serialNumber, props.scope]),
  queryFn: async () => {
    const params = new URLSearchParams({ serial_number: props.serialNumber, date_from: dateFrom, date_till: dateTill, scope: props.scope })
    const result = await fetchData(`${READINGS_API_BASE}/month-aggregates?${params}`)
    return result?.status === 'OK' ? result.data : []
  },
  refetchOnWindowFocus: false,
  staleTime: 30 * 60 * 1000,
  enabled: computed(() => expanded.value),
})

const analysis = computed(() => {
  if (!monthlyData.value) return null
  return analyzeFiveYear({
    monthlyData: monthlyData.value,
    mainWp: props.mainWp,
    expectedProduction: props.expectedProduction,
    dateActivated: props.dateActivated,
  })
})

// Degradation display
const degradationSegment = computed(() =>
  analysis.value?.segments?.find(s => s.text?.includes('degradatie') || s.text?.includes('afname') || s.text?.includes('Stabiele'))
)

const degradationLabel = computed(() => {
  const s = degradationSegment.value
  if (!s) return '—'
  if (s.text.includes('Stabiele') || s.text.includes('Normale')) return 'Normaal'
  if (s.text.includes('Lichte')) return 'Licht'
  if (s.text.includes('significant') || s.text.includes('boven de normale')) return 'Hoog'
  return 'Onbekend'
})

const degradationDetail = computed(() => {
  const s = degradationSegment.value
  if (!s) return 'onvoldoende data'
  if (s.text.includes('%/jaar')) {
    const match = s.text.match(/([\d.]+)%\/jaar/)
    return match ? `${match[1]}%/jaar` : ''
  }
  return s.type === 'good' ? 'geen significante afname' : ''
})

const degradationStatus = computed(() => {
  const s = degradationSegment.value
  if (!s) return 'neutral'
  return s.type === 'critical' ? 'critical' : s.type === 'warning' ? 'warning' : 'good'
})

const degradationBg = computed(() => STATUS_COLORS[degradationStatus.value]?.bg || STATUS_COLORS.neutral.bg)
const degradationIcon = computed(() => STATUS_COLORS[degradationStatus.value]?.icon || STATUS_COLORS.neutral.icon)
const degradationTextColor = computed(() => STATUS_COLORS[degradationStatus.value]?.text || STATUS_COLORS.neutral.text)
const degradationSubColor = computed(() => STATUS_COLORS[degradationStatus.value]?.sub || STATUS_COLORS.neutral.sub)

const ratioBar = ratioBarColor
const ratioColor = ratioTextColor
</script>
